import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, Shield, ShieldCheck, ShieldX, ShieldAlert,
  RefreshCw, CheckCircle, XCircle, Clock, Link, Lock, Unlock,
  ChevronDown, ChevronUp, AlertTriangle
} from 'lucide-react';

const SSLMonitor = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [showChain, setShowChain] = useState(false);

  useEffect(() => {
    if (selectedSite) {
      fetchData();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [selectedSite]);

  const fetchData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/ssl-monitor.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to fetch SSL data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <ShieldCheck className="h-16 w-16 text-green-500" />;
      case 'warning':
        return <ShieldAlert className="h-16 w-16 text-yellow-500" />;
      case 'critical':
      case 'expired':
        return <ShieldX className="h-16 w-16 text-red-500" />;
      default:
        return <Shield className="h-16 w-16 text-gray-400" />;
    }
  };

  const getStatusMessage = (status, days) => {
    switch (status) {
      case 'valid':
        return `Certificate is valid for ${days} more days`;
      case 'warning':
        return `Certificate expires in ${days} days - Consider renewing soon`;
      case 'critical':
        return `Certificate expires in ${days} days - Renew immediately!`;
      case 'expired':
        return `Certificate has expired ${Math.abs(days)} days ago!`;
      default:
        return 'Certificate status unknown';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'valid':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'critical':
      case 'expired':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Checking SSL certificate...</p>
        </div>
      </div>
    );
  }

  if (!selectedSite) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-yellow-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <p className="text-yellow-800">Please select a site</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-8 w-8 text-blue-600" />
              SSL Certificate Monitor
            </h1>
            <p className="text-gray-600 mt-1">
              Monitor SSL certificate status and security headers
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-6">
            {/* SSL Not Enabled */}
            {!data.ssl_enabled && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
                <Unlock className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-red-700 mb-2">SSL Not Enabled</h2>
                <p className="text-red-600 mb-4">
                  {data.error || 'This site does not have a valid SSL certificate'}
                </p>
                <p className="text-sm text-red-500">
                  SSL certificates are essential for secure e-commerce. Consider installing one immediately.
                </p>
              </div>
            )}

            {/* SSL Status Card */}
            {data.ssl_enabled && data.certificate && (
              <>
                <div className={`rounded-lg border p-6 ${getStatusBgColor(data.status)}`}>
                  <div className="flex items-center gap-6">
                    {getStatusIcon(data.status)}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Lock className="h-6 w-6" />
                        {data.host}
                      </h2>
                      <p className={`text-lg mt-1 ${
                        data.status === 'valid' ? 'text-green-700' :
                        data.status === 'warning' ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>
                        {getStatusMessage(data.status, data.certificate.days_until_expiry)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Certificate Details */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Certificate Details</h3>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Subject */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Subject (Your Site)</h4>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Common Name</span>
                            <span className="font-medium text-gray-900">{data.certificate.subject.common_name || 'N/A'}</span>
                          </div>
                          {data.certificate.subject.organization && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Organization</span>
                              <span className="font-medium text-gray-900">{data.certificate.subject.organization}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Issuer */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-2">Issuer (Certificate Authority)</h4>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Common Name</span>
                            <span className="font-medium text-gray-900">{data.certificate.issuer.common_name || 'N/A'}</span>
                          </div>
                          {data.certificate.issuer.organization && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Organization</span>
                              <span className="font-medium text-gray-900">{data.certificate.issuer.organization}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Validity */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Validity Period</h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-3">
                            <Clock className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                            <p className="text-xs text-gray-500">Valid From</p>
                            <p className="font-medium text-gray-900">{data.certificate.valid_from}</p>
                          </div>
                          <div className="text-center p-3">
                            <Clock className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                            <p className="text-xs text-gray-500">Valid Until</p>
                            <p className="font-medium text-gray-900">{data.certificate.valid_to}</p>
                          </div>
                          <div className={`text-center p-3 rounded-lg ${
                            data.certificate.is_expired ? 'bg-red-100' :
                            data.certificate.days_until_expiry <= 30 ? 'bg-yellow-100' :
                            'bg-green-100'
                          }`}>
                            <Clock className={`h-5 w-5 mx-auto mb-1 ${
                              data.certificate.is_expired ? 'text-red-500' :
                              data.certificate.days_until_expiry <= 30 ? 'text-yellow-500' :
                              'text-green-500'
                            }`} />
                            <p className="text-xs text-gray-500">Days Remaining</p>
                            <p className={`text-2xl font-bold ${
                              data.certificate.is_expired ? 'text-red-600' :
                              data.certificate.days_until_expiry <= 30 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {data.certificate.is_expired ? 'EXPIRED' : data.certificate.days_until_expiry}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-500">Signature Algorithm</span>
                        <p className="font-mono text-sm text-gray-900">{data.certificate.signature_algorithm || 'N/A'}</p>
                      </div>
                      {data.certificate.san && (
                        <div>
                          <span className="text-sm text-gray-500">Subject Alt Names</span>
                          <p className="font-mono text-xs text-gray-900 break-all">{data.certificate.san}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Certificate Chain */}
                {data.chain && data.chain.length > 0 && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <button
                      className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50"
                      onClick={() => setShowChain(!showChain)}
                    >
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Link className="h-5 w-5 text-blue-600" />
                        Certificate Chain ({data.chain.length} certificates)
                      </h3>
                      {showChain ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    {showChain && (
                      <div className="p-6">
                        <div className="space-y-3">
                          {data.chain.map((cert, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{cert.subject}</p>
                                <p className="text-sm text-gray-500">Issued by: {cert.issuer}</p>
                              </div>
                              <div className="text-sm text-gray-500">
                                Expires: {cert.valid_to}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Security Headers */}
                {data.security_headers && data.security_headers.length > 0 && (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Security Headers</h3>
                      <p className="text-sm text-gray-500">Additional HTTP security configurations</p>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {data.security_headers.map((header, idx) => (
                        <div key={idx} className="px-6 py-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {header.present ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-400" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{header.name}</p>
                              <p className="text-sm text-gray-500">{header.description}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {header.present ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Enabled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Missing
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Header Score */}
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Security Header Score</span>
                        <span className={`text-lg font-bold ${
                          data.security_headers.filter(h => h.present).length >= 5 ? 'text-green-600' :
                          data.security_headers.filter(h => h.present).length >= 3 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {data.security_headers.filter(h => h.present).length} / {data.security_headers.length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SSLMonitor;
